import React, {
  CSSProperties, forwardRef, HTMLAttributes, PropsWithChildren,
  ReactNode, ReducerWithoutAction, Ref, RefObject, useCallback, useEffect, useMemo, useReducer,
  useRef, useState
} from 'react';
import { styled } from '@mui/material/styles';
import MuiCheckbox, { CheckboxProps } from '@mui/material/Checkbox';
import debounce from '@bimdata/client/utils/debounce';

import Intersected from '@/components/intersections/Intersected';
import Intersecter from '@/components/intersections/Intersecter';
import ChevronDown from '@/components/icons/Chevron';

import hinted from './Hinted';
import { AmountsLabel } from './AmountLabel';
import Collapse from '@mui/material/Collapse';
import Checked from './icons/Checked';
import Indeterminate from './icons/Indeterminate';
import Unchecked from './icons/Unchecked';
import CrossIcon from './icons/Cross';

const Checkbox = (props: Omit<CheckboxProps, 'checkedIcon' | 'indeterminateIcon'>) => (
  <MuiCheckbox
    {...props}
    checkedIcon={<Checked size={18} color='#0052CC'/>}
    indeterminateIcon={<Indeterminate size={18} color='#0052CC'/>}
    icon={<Unchecked size={18} color='#C1C7D0'/>}
    sx={{ padding: 0 }}
  />
);

type ColumnProps = {
  attrName?: string;
  sortable?: true;
  initialSorting?: 'A' | 'D';
  sorting?: 'A' | 'D';
  width?: string | number;
  onSort?: (attrName: string, order: 'A' | 'D') => void;
  style?: CSSProperties;
} & PropsWithChildren;

const TableHeader = styled('thead')({
  position: 'sticky',
  top: 0,
  zIndex: 2,
});

const CheckListToolBar = styled('div')({
  background: '#0052CC',
  height: '44px',
  padding: '6px 14px',
  borderBottom: '1px solid #EBECF0',
  color: '#FFFFFF',
  display: 'flex',
  gap: '20px',
  alignItems: 'center'
});

const SelectionStatus = styled('span')({
  fontWeight: 400,
  fontSize: '14px',
  '&>b': {
    fontWeight: 700,
  }
});

const Toolbox = styled('div')({
  display: 'flex',
  gap: '10px',
  flexGrow: 1,
});

const HeaderCell = styled('th')(({ clickable }: { clickable: boolean }) => ({
  borderBottom: '1px solid #EBECF0',
  backgroundColor: '#FFFFFF',
  height: '37px',
  color: '#6B778C',
  fontSize: '12px',
  fontWeight: 400,
  textAlign: 'left',
  padding: '6px 0',
  textOverflow: 'ellipsis',
  ...(clickable ? {
    //cursor: 'pointer',
    '&:hover': {
      color: '#091E42',
      background: '#FAFAFA',

      '& svg': {
        fill: '#0065FF',
        display: 'initial',
      }
    }
  } : {}),
}));

const HeaderChevron = styled(ChevronDown)({
  flexShrink: 0,
});

const GroupTogglerContent = styled('div')({
  padding: '6px',
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
});

const InlineChevron = styled(ChevronDown)({
  verticalAlign: 'middle',
});

const HeaderText = styled('div')({
  textOverflow: 'ellipsis',
  overflowX: 'clip',
  textWrap: 'nowrap',
  minWidth: '0px',
});

const HeaderFlex = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  borderLeft: '1px solid #EBECF0',
  padding: '0px 8px 0px 10px',
  height: '100%',
  alignItems: 'center',
});

const ToolbarCloseButton = styled('button')({
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  height: '32px',
  width: '32px',
  lineHeight: '32px',
  '&>*': {
    verticalAlign: 'middle'
  }
});

const Header = function({ attrName, initialSorting, sorting, children, onSort, style }: ColumnProps) {
  const handleClick = useCallback(() => {
    if (onSort && attrName) {
      onSort(attrName, !sorting ? (initialSorting || 'A') : (sorting === 'A' ? 'D' : 'A'));
    }
  }, [ attrName, onSort, sorting, initialSorting ]);

  return <HeaderCell
    clickable={!!attrName && !!onSort}
    onClick={handleClick}
    style={style}
  >
    {children && (
      <HeaderFlex>
        {typeof children === 'string'
          ? <HeaderText>{children}</HeaderText>
          : children
        }
        <HeaderChevron fill="#42526E" size={16} rotation={sorting === 'A' ? 180 : 0} display={sorting ? 'initial' : 'none'}/>
      </HeaderFlex>
    )}
  </HeaderCell>
};

const TableContainer = Intersected(styled('div')({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  border: '1px solid #EBECF0',
}));

const Table = styled('table')({
  width: '100%',
  //tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
});

type ColumnType = 'iconic' | 'default';

const Cell = styled('td')(({ type = 'default' }: { type?: ColumnType }) =>({
  color: '#091E42',
  fontWeight: '400',
  fontSize: '13px',
  borderBottom: '1px solid #EBECF0',
  padding: '6px',
  height: '44px',
  textWrap: 'nowrap',
  overflowX: 'clip',
  ...(type === 'iconic' ? {
    textAlign: 'center',
  } : {
    textOverflow: 'ellipsis',
  })
}));

const HintedCell = hinted(Cell, {
  showCondition: (e) => e.offsetWidth < e.scrollWidth,
  placement: 'auto',
});

const GroupTitle = styled('tr')`
  background: #FAFAFA;

  & td {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    color: #091E42;
    border-bottom: 1px solid #EBECF0;
    padding: 6px 0;
    vertical-align: middle;
    cursor: pointer;
  }
`;

type GroupTogglerProps<T> = {
  group: RowGroup<T>;
  onToggle?: (code: string) => void;
} & PropsWithChildren;

function GroupToggler<T>({ group, children, onToggle }: GroupTogglerProps<T>) {
  const onClick = useCallback(() => onToggle && onToggle(group.code), [group, onToggle]);
  return (
    <GroupTitle
      className='group-toggler'
      key={`group-${group.code}-toggler`}
      onClick={onToggle && onClick}
    >
      { children }
    </GroupTitle>
  );
};

const PageLoader = Intersecter('tbody');

type DataGetter = (item: unknown) => ReactNode;

export enum ColumnWidthType {
  Fill = 'FILL',
  Hug = 'HUG'
}

export type ColumnMeta = {
  attrName?: string;
  noSort?: true;
  defaultSorting?: 'A' | 'D';
  width?: ColumnWidthType | number;
  title?: ReactNode;
  dataGetter?: DataGetter;
  type?: ColumnType;
  style?: CSSProperties;
  headerStyle?: CSSProperties;
  cellStyle?: CSSProperties;
  hinted?: true;
};

const SimpleRow = styled('tr')((props: { checked?: boolean }) => ({
  ...(props.checked ? {
    opacity: '1 !important',
    '& td': {
      backgroundColor: '#E6F5FF',
    }
  } : {}),
  '&:hover': {
    opacity: '1 !important',
    '& td': {
      backgroundColor: '#F8FCFF',
    }
  },
}));

const RawRow = Intersecter(SimpleRow);

type ComplextRowProps = HTMLAttributes<HTMLTableRowElement> & {
  raw: boolean;
  onVisible: (entry: IntersectionObserverEntry) => void;
  checked?: boolean;
};

const ComplexRow = forwardRef(({ raw, onVisible, children, ...props }: ComplextRowProps, ref: Ref<HTMLTableRowElement>) => (
  raw ? (<RawRow ref={ref} once={true} onIntersect={onVisible} {...props}>{children}</RawRow>) : (<SimpleRow ref={ref} {...props}>{children}</SimpleRow>)
));

type RowStyle = CSSProperties | ((item: unknown) => CSSProperties | undefined);

type RowProps = {
  item: unknown;
  container: RefObject<HTMLDivElement | null> | null;
  dataWindowEnlargement?: number;
  columns: ColumnMeta[];
  onFirstDisplay?: (item: unknown) => void;
  style?: RowStyle;
  checked?: boolean,
  onCheck?: () => void
}

const Row = function({ item, columns, onFirstDisplay, container, dataWindowEnlargement, style, checked, onCheck }: RowProps) {
  const [prevItem, setItem] = useState(item);
  const [displayHandled, setDisplayHandled] = useState<boolean>(onFirstDisplay ? false : true);

  const ref = useRef<HTMLTableRowElement>(null);

  const onVisible = useCallback((entry: IntersectionObserverEntry) => {
    if (onFirstDisplay) {
      onFirstDisplay(item);
      setDisplayHandled(true);
    }
  }, [item, onFirstDisplay]);

  useEffect(() => {
    if (item != prevItem) {
      setDisplayHandled(onFirstDisplay ? false : true);
      setItem(item);
    }
  }, [item, prevItem]);

  const low = (container && container.current) ? container.current.scrollTop - (dataWindowEnlargement || 0) : 0;
  const high = (container && container.current)
    ? container.current.scrollTop + container.current.offsetHeight + (dataWindowEnlargement || 0)
    : 0;

  const rowStyle = (typeof style === 'function') ? style(item) : style;

  return (
    <ComplexRow ref={ref} raw={!displayHandled} onVisible={onVisible} style={rowStyle} checked={checked}>
      {!!onCheck && (
        <Cell key="row-checker" type="iconic" style={SelectionColumnStyles}>
          <Checkbox
            checked={!!checked}
            onClick={onCheck}
          />
        </Cell>
      )}
      {
        columns.map(({ attrName, dataGetter, style, cellStyle, hinted, type, width }, i) => {
          const joinedStyles = {
            ...style,
            ...cellStyle,
          };
          if (
            !container ||
            (ref.current && container.current &&
            (ref.current.offsetTop < high) &&
            ((ref.current.offsetTop + ref.current.offsetHeight) > low))
          ) {
            const data = dataGetter ? dataGetter(item) : (attrName ? (item as Record<string, string | number | null | boolean>)[attrName] : '');
            if (hinted) {
              return (<HintedCell key={`c-${i}`} type={type} style={joinedStyles} hint={data?.toString()}>{data}</HintedCell>);
            }
            return (<Cell key={`c-${i}`} type={type} style={joinedStyles}>{data}</Cell>);
          } else {
            return (<Cell key={`c-${i}`} type={type} style={joinedStyles}>&nbsp;</Cell>);
          }
        })
      }
    </ComplexRow>
  );
};

type ItemId = string | number;

export type TableListProps<T> = {
  columns: ColumnMeta[];
  sorting?: [string, 'A' | 'D'] | null;
  data?: T[] | RowGroup<T>[];
  pageSize?: number;
  grouped?: true;
  hideEmptyGroups?: true;
  onViewportResize?: (e: HTMLElement) => void;
  onDataRequest?: (offset: number, size: number | undefined) => Promise<void>;
  onRowVisible?: (item: any) => void;
  onSortingChange?: (by: string, order: 'A' | 'D') => void;
  rowStyle?: RowStyle;
  nextPageThreshold?: number;
  windowed?: true;
  dataWindowScale?: number;
  scrollDebounceDelay?: number;
  idGetter?: (item: T) => ItemId;
  isCheckList ?: boolean;
  checkListChange?: (items: T[]) => void;
  selectionStatusCaption?: string;
};

type RowGroup<T> = {
  code: string;
  title: string;
  data: T[];
};

const SelectionColumnStyles: CSSProperties = {
  maxWidth: '26px',
  minWidth: '26px',
  width: '26px',
  textAlign: 'center',
  padding: '6px 0',
};


function forEachItem<T>(
  data: T[] | RowGroup<T>[] | undefined,
  grouped: boolean,
  callback: (item: T) => void
): void {
  if (!data || !data.length) {
    return;
  }
  const groups = grouped
    ? (data as RowGroup<T>[])
    : [
      { data: (data as T[]) }
    ];
  for (const g of groups) {
    for (const item of g.data) {
      callback(item);
    }
  }
};

export default function<T>(
  {
    columns: rawColumns,
    data: rawData,
    grouped,
    sorting,
    pageSize,
    hideEmptyGroups,
    onViewportResize,
    onDataRequest,
    onRowVisible,
    onSortingChange,
    nextPageThreshold,
    windowed,
    scrollDebounceDelay,
    dataWindowScale,
    rowStyle,
    isCheckList = false,
    checkListChange,
    selectionStatusCaption,
    idGetter,
    children,
    ...containerProps
  }: TableListProps<T> & HTMLAttributes<HTMLDivElement>
) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [collapsedGroups, setCollapsed] = useState<string[]>([]);

  const [checked] = useState<Map<ItemId, T>>(new Map());

  const [showToolbar, setShowToolbar] = useState(false);

  const [forceRerenderVal, forceRerender] = useReducer(
    (prev) => prev < Number.MAX_SAFE_INTEGER ? prev + 1 : Number.MIN_SAFE_INTEGER,
    Number.MIN_SAFE_INTEGER
  );

  const setSorting = useCallback((by: string, order: 'A' | 'D') => {
    if (!!onSortingChange) onSortingChange(by, order);
  }, [onSortingChange]);

  const getId = useCallback((item: T, strict: boolean = true) => {
    const id = (idGetter && idGetter(item)) || (item as any).id;
    if (!id && !!strict) {
      throw new Error('Unable to identify the item.');
    }
    return id;
  }, [ idGetter ]);

  const enlargement = containerRef.current ? containerRef.current.offsetHeight * (Math.max(dataWindowScale || 1, 1) - 1) / 2 : 0;

  const columns: ColumnMeta[] = useMemo(() => {
    const fillColumnsCount = rawColumns.reduce((acc, { width }) => (acc + Number(!width || width === ColumnWidthType.Fill)), 0);
    return rawColumns.map(({ width, headerStyle, cellStyle, ...props }) => {
      const widthStyle: CSSProperties = (typeof width === 'undefined' || width === ColumnWidthType.Fill)
        ? {
          maxWidth: '1px',
          width: `${Math.round(100/fillColumnsCount)}%`,
        }
        : (
          (width === ColumnWidthType.Hug)
            ? { width: '1px' }
            : (
              Number.isFinite(width)
                ? {
                  maxWidth: `${width}px`,
                  minWidth: `${width}px`,
                  width: `${width}px`,
                }
                : {}
            )
        );

      return {
        ...props,
        cellStyle: { ...widthStyle, ...cellStyle },
        headerStyle: { ...widthStyle, ...headerStyle },
        width,
      };
    });
  }, [ rawColumns ]);

  const { data, total } = useMemo(() => {
    if (grouped && hideEmptyGroups) {
      let total = 0;
      const data = (rawData as RowGroup<T>[]).filter((g) => {
        total += g.data.length;
        return !!g.data.length;
      });
      return { data, total };
    }
    return { data: rawData, total: rawData?.length || 0 };
  }, [ rawData, grouped, hideEmptyGroups ]);

  //При изменении data, обеспечиваем обновление элементов checked
  // и передачу обновлённых элементов в checkListChange
  //Намеренно мутируем замкнутый checked, чтобы избежать ререндера
  useEffect(() => {
    if (isCheckList) {
      const ids = new Set<ItemId>(checked.keys());
      checked.clear();
      forEachItem(data || [], !!grouped, (item) => {
        const id = getId(item);
        if (ids.has(id)) {
          checked.set(id, item);
        }
      });
      checkListChange && checkListChange(Array.from(checked.values()));
    }
  }, [ data ]);

  const onChecked = useCallback(() => {
    if (!!checked.size) {
      setShowToolbar(true);
    }
    checkListChange && checkListChange(Array.from(checked.values()));
    forceRerender();
  }, [ checkListChange ]);

  const closeToolbarHandler = useCallback(() => {
    setShowToolbar(false);
    checked.clear();
    checkListChange && checkListChange([]);
    forceRerender();
  }, [ checkListChange ]);

  const allChecked = checked.size ? (total === checked.size ? true : null) : false;

  const groupCheckStatus = useMemo(() => {
    let stats = new Map<string, { status: boolean | null; checked: number }>();
    if (data && grouped) {
      (data as RowGroup<T>[]).forEach(({ code, data }) => {
        const cc = data.reduce((s, item) => s + Number(checked.has(getId(item))), 0);
        stats.set(code, { status: cc ? (data.length === cc ? true : null) : false, checked: cc });
      });
    }
    return stats;
  }, [ data, grouped, getId, forceRerenderVal ]);

  const toggleElementCheck = useCallback((item: T) => {
    const id = getId(item);
    if (!checked.has(id)) {
      checked.set(id, item);
    } else {
      checked.delete(id);
    }
    onChecked();
  }, [ getId, onChecked ]);

  const toggleCheckAll = useCallback(() => {
    if (allChecked) {
      checked.clear();
    } else {
      forEachItem(data || [], !!grouped, (item) => checked.set(getId(item), item));
    }
    onChecked();
  }, [ allChecked, data, grouped, getId, onChecked ]);

  const toggleGroupCheck = useCallback((g: RowGroup<T>) => {
    if (!grouped) {
      return;
    }
    const status = groupCheckStatus.get(g.code)?.status;
    for (const item of g.data) {
      const id = getId(item);
      if (!status) {
        checked.set(id, item);
      } else {
        checked.delete(id);
      }
    }
    onChecked();
  }, [ data, grouped, groupCheckStatus, checked, getId, onChecked ]);

  const renderRow = useCallback((item: T, i?: number) => {
    const id = getId(item, isCheckList) || i;
    return <Row
      key={id && `r-${id}`}
      item={item}
      columns={columns}
      onFirstDisplay={onRowVisible}
      container={windowed ? containerRef : null}
      dataWindowEnlargement={enlargement}
      style={rowStyle}
      checked={isCheckList ? checked.has(id) : undefined}
      onCheck={isCheckList ? () => toggleElementCheck(item) : undefined}
    />;
  }, [ isCheckList, checked, columns ])

  const onDataNeeded = useCallback(() => {
    if (!!onDataRequest && pageSize) {
      onDataRequest(data?.length || 0, pageSize);
    }
  }, [data, pageSize, onDataRequest]);

  const scrollHandler = useCallback(debounce(forceRerender, scrollDebounceDelay || 10), [scrollDebounceDelay]);

  const onGroupToggle = useCallback((group: string) => {
    const collapsed = collapsedGroups.includes(group);
    setCollapsed(
      collapsed
        ? collapsedGroups.filter((c) => c !== group)
        : [ ...collapsedGroups, group]
    );
    if (windowed && !collapsed) {
      forceRerender();
    }
  }, [collapsedGroups, windowed]);

  useEffect(() => {
    if (!data) {
      onDataNeeded();
    } else if (windowed) {
      forceRerender();
    }
  }, [data, windowed])

  useEffect(() => {
    if (containerRef.current) {
      if (onViewportResize) {
        containerRef.current.addEventListener('resize', (e) => {
          if (e.target) {
            onViewportResize(e.target as HTMLElement);
          }
        });
        onViewportResize(containerRef.current as HTMLElement);
      }
    }
  }, [containerRef.current, onViewportResize]);

  return (
  <TableContainer
    ref={containerRef}
    threshold={nextPageThreshold}
    onScroll={windowed ? scrollHandler : undefined}
    {...containerProps}
  >
  <Table>
    <TableHeader>
      <tr>
        {isCheckList && (
          <HeaderCell
            clickable={false}
            style={SelectionColumnStyles}
          >
            <Checkbox
              checked={!!allChecked}
              indeterminate={allChecked === null}
              onClick={toggleCheckAll}
            />
          </HeaderCell>
        )}
        {
          columns.map(({ attrName, title, defaultSorting, style, headerStyle, width, noSort }, i) => (
            <Header
              key={`h-${i}`}
              attrName={attrName}
              initialSorting={defaultSorting}
              sorting={sorting && attrName === sorting[0] ? sorting[1] : undefined}
              onSort={!noSort ? setSorting : undefined}
              style={{
                ...style,
                ...headerStyle,
              }}
            >
              {title}
            </Header>
          ))
        }
      </tr>
      {isCheckList
        && (
          <tr>
            <th style={{ padding: 0 }} colSpan={columns.length + 1}>
              <Collapse in={showToolbar} easing="linear" timeout={200}>
                <CheckListToolBar>
                  <SelectionStatus>
                    {selectionStatusCaption && `${selectionStatusCaption}:\u00A0`}<b>{checked.size}</b>
                  </SelectionStatus>
                  <Toolbox>{children}</Toolbox>
                  <ToolbarCloseButton onClick={closeToolbarHandler}>
                    <CrossIcon size={18} color="#FFFFFF"/>
                  </ToolbarCloseButton>
                </CheckListToolBar>
              </Collapse>
            </th>
          </tr>
        )
      }
    </TableHeader>
    {
      data && (
        grouped
          ? (
            (data as RowGroup<T>[]).filter((g) => !hideEmptyGroups || !!g.data.length).map((g) => {
              const collapsed = collapsedGroups.includes(g.code);
              const groupChecked = groupCheckStatus.get(g.code);
              return (<tbody key={`group-${g.code}`}>
                <GroupToggler
                  group={g}
                  onToggle={onGroupToggle}
                >
                  {isCheckList && (
                    <td style={SelectionColumnStyles}>
                      <Checkbox
                        checked={!!groupChecked?.status}
                        indeterminate={groupChecked?.status === null}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroupCheck(g);
                        }}
                      />
                    </td>
                  )}
                  <td colSpan={columns.length}>
                    <GroupTogglerContent>
                      <InlineChevron fill="#091E42" size={24} rotation={collapsed ? -90 : 0} />
                      {g.title}
                      <AmountsLabel style={{ margin: '0 4px' }}>
                        {!!groupChecked && (<><b>{groupChecked.checked}</b>/</>)}
                        {g.data.length}
                        </AmountsLabel>
                    </GroupTogglerContent>
                  </td>
                </GroupToggler>
                {!collapsed && g.data.map(renderRow)}
              </tbody>);
            })
          )
          : (
            (!!onDataRequest && !!pageSize) ? (
              <>
                <tbody>{(data as Array<T>).slice(0, data.length - pageSize!).map(renderRow)}</tbody>
                <PageLoader onIntersect={onDataNeeded}>{(data as Array<T>).slice(data.length - pageSize!).map(renderRow)}</PageLoader>
              </>
            ) : (
              <tbody>{(data as Array<T>).map(renderRow)}</tbody>
            )
          )
      )
    }
  </Table>
</TableContainer>)
};
